import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Briefcase, FileArrowUp, X, CheckCircle, XCircle, Clock, FileText } from "@phosphor-icons/react";

const ACCEPT = ".csv,.xls,.xlsx,.pdf,.png,.jpg,.jpeg";
const MAX_FILES = 5;
const MAX_MB = 10;

const STATUS_META = {
  PENDING:  { label: "Under review", color: "#FCD535", Icon: Clock },
  APPROVED: { label: "Approved",     color: "#0ECB81", Icon: CheckCircle },
  REJECTED: { label: "Rejected",     color: "#F6465D", Icon: XCircle },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.PENDING;
  const { Icon } = m;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold"
      style={{ backgroundColor: `${m.color}1A`, color: m.color }} data-testid={`prop-status-${status}`}>
      <Icon size={14} weight="fill" /> {m.label}
    </span>
  );
}

export default function PropPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ first_name: "", last_name: "", account_id: "", prop_firm: "", comment: "" });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/prop/my");
      setRequests(r.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const hasPending = requests.some((r) => r.status === "PENDING");

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const merged = [...files, ...picked].slice(0, MAX_FILES);
    const tooBig = merged.find((f) => f.size > MAX_MB * 1024 * 1024);
    if (tooBig) { toast.error(`${tooBig.name} exceeds ${MAX_MB}MB`); }
    setFiles(merged.filter((f) => f.size <= MAX_MB * 1024 * 1024));
    e.target.value = "";
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.account_id.trim() || !form.prop_firm.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("first_name", form.first_name.trim());
      fd.append("last_name", form.last_name.trim());
      fd.append("account_id", form.account_id.trim());
      fd.append("prop_firm", form.prop_firm.trim());
      fd.append("comment", form.comment.trim());
      files.forEach((f) => fd.append("files", f));
      await api.post("/prop/request", fd);
      toast.success("Prop account request submitted");
      setForm({ first_name: "", last_name: "", account_id: "", prop_firm: "", comment: "" });
      setFiles([]);
      load();
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSubmitting(false); }
  };

  const field = (key, label, placeholder, required = true) => (
    <div>
      <label className="overline text-[#9CA3AF]">{label}{required && <span className="text-[#F6465D]"> *</span>}</label>
      <input value={form[key]} onChange={set(key)} placeholder={placeholder}
        className="w-full h-10 px-3 mt-1 bg-[#111827] border border-[#1F2937] focus:border-[#FCD535] outline-none rounded-md text-sm"
        data-testid={`prop-input-${key}`} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0E17] text-white pb-24" data-testid="prop-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#FCD535] flex items-center justify-center rounded-sm">
            <Briefcase size={20} weight="fill" className="text-black" />
          </div>
          <div>
            <p className="overline text-[#FCD535]">Funding</p>
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight leading-tight">Request Prop Account</h1>
          </div>
        </div>

        {/* Existing requests */}
        {!loading && requests.length > 0 && (
          <div className="mb-6 space-y-2" data-testid="prop-requests-list">
            {requests.map((r) => (
              <div key={r.id} className="bg-[#0D1117] border border-[#1F2937] rounded-md p-4" data-testid={`prop-request-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{r.prop_firm}</div>
                    <div className="text-xs text-[#6B7280] mono truncate">
                      {r.first_name} {r.last_name} · Acc: {r.account_id}
                    </div>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
                {r.admin_note && r.status === "REJECTED" && (
                  <div className="mt-2 text-xs text-[#F6465D]">Reason: {r.admin_note}</div>
                )}
                {r.files?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {r.files.map((f) => (
                      <a key={f.id} href={`${api.defaults.baseURL}/prop/file/${f.id}`}
                        onClick={(e) => { e.preventDefault(); downloadFile(f); }}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[#1F2937] text-[#9CA3AF] hover:text-white text-[11px]"
                        data-testid={`prop-file-${f.id}`}>
                        <FileText size={12} /> {f.original_filename}
                      </a>
                    ))}
                  </div>
                )}
                <div className="mt-2 text-[10px] text-[#6B7280] mono">{new Date(r.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}

        {/* Form (hidden while a request is pending) */}
        {loading ? (
          <div className="bg-[#0D1117] border border-[#1F2937] rounded-md p-8 flex items-center justify-center gap-2 text-sm text-[#6B7280]" data-testid="prop-loading">
            <Clock size={16} className="animate-spin" /> Loading…
          </div>
        ) : hasPending ? (
          <div className="bg-[#0D1117] border border-[#FCD535]/30 rounded-md p-5 text-center" data-testid="prop-pending-notice">
            <Clock size={28} weight="fill" className="text-[#FCD535] mx-auto mb-2" />
            <p className="text-sm text-[#D1D5DB]">Your request is under review. You'll be notified once the admin approves or rejects it.</p>
          </div>
        ) : (
          <div className="bg-[#0D1117] border border-[#1F2937] rounded-md p-5 space-y-4" data-testid="prop-form">
            <p className="text-sm text-[#9CA3AF]">
              Fill in your details and upload your trade history for review. If approved, the admin will fund your trading account.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("first_name", "First name", "John")}
              {field("last_name", "Last name", "Doe")}
              {field("account_id", "Trading account number / ID", "e.g. 5012345")}
              {field("prop_firm", "Prop firm", "e.g. FTMO")}
            </div>
            <div>
              <label className="overline text-[#9CA3AF]">Comment (optional)</label>
              <textarea value={form.comment} onChange={set("comment")} rows={2} placeholder="Anything the admin should know…"
                className="w-full px-3 py-2 mt-1 bg-[#111827] border border-[#1F2937] focus:border-[#FCD535] outline-none rounded-md text-sm resize-none"
                data-testid="prop-input-comment" />
            </div>

            {/* File upload */}
            <div>
              <label className="overline text-[#9CA3AF]">Trade history (CSV, Excel, PDF, PNG, JPG · max {MAX_FILES} files, {MAX_MB}MB each)</label>
              <label className="mt-1 flex items-center justify-center gap-2 h-20 border-2 border-dashed border-[#1F2937] rounded-md cursor-pointer hover:border-[#FCD535]/50 transition-colors"
                data-testid="prop-file-dropzone">
                <FileArrowUp size={22} className="text-[#9CA3AF]" />
                <span className="text-sm text-[#9CA3AF]">Tap to select files</span>
                <input type="file" multiple accept={ACCEPT} onChange={onFiles} className="hidden" data-testid="prop-file-input" />
              </label>
              {files.length > 0 && (
                <div className="mt-2 space-y-1.5" data-testid="prop-selected-files">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between bg-[#111827] border border-[#1F2937] rounded px-2.5 py-1.5">
                      <span className="text-xs truncate mr-2">{f.name} <span className="text-[#6B7280]">({(f.size / 1024 / 1024).toFixed(1)}MB)</span></span>
                      <button onClick={() => removeFile(i)} className="text-[#9CA3AF] hover:text-[#F6465D] shrink-0" data-testid={`prop-remove-file-${i}`}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button onClick={submit} disabled={submitting}
              className="w-full h-11 bg-[#FCD535] hover:bg-[#FCD535]/90 text-black font-semibold rounded-md"
              data-testid="prop-submit-button">
              {submitting ? "Submitting…" : "Submit request"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  async function downloadFile(f) {
    try {
      const res = await api.get(`/prop/file/${f.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatErr(err));
    }
  }
}
